import { useEffect, useMemo } from 'react'

import {
  CompetenciesCompetenciesFinderSearchDocument,
  CompetenciesFinderFactorialCompetencyFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'

type UseSearchParams = {
  canSearch: boolean
  excludeUuids?: string[]
  search: string
}

type UseSearchReturn = {
  suggestions: CompetenciesFinderFactorialCompetencyFragment[]
  error: boolean
  loading: boolean
  loadingQuantity: number
}

const SEARCHES_PER_PAGE = 8
export const useFactorialCompetenciesSearch = ({
  search = '',
  canSearch = false,
  excludeUuids,
}: UseSearchParams): UseSearchReturn => {
  const { company } = useCurrentSession()

  const [fetchSearch, { loading, error, data }] = useMutation(
    CompetenciesCompetenciesFinderSearchDocument,
    {
      extractorFn: (d) => d.competenciesMutations.searchCompetenciesFactorialCompetency,
    }
  )

  const excludeUuidsString = useMemo(() => {
    return (excludeUuids || []).sort().join(',')
  }, [excludeUuids])

  useEffect(() => {
    async function loadInitial() {
      await fetchSearch({
        variables: {
          companyId: company.id,
          excludeUuids: excludeUuidsString ? excludeUuidsString.split(',') : [],
          search: search,
        },
      })
    }

    if (canSearch) {
      loadInitial()
    }
  }, [company.id, search, excludeUuidsString, canSearch, fetchSearch])

  const suggestions =
    data?.competenciesMutations?.searchCompetenciesFactorialCompetency?.factorialCompetencies || []

  return {
    suggestions: suggestions.slice(0, SEARCHES_PER_PAGE),
    error: !!error,
    loading: loading,
    loadingQuantity: SEARCHES_PER_PAGE,
  }
}
